/**
 * Runtime validation for Knowledge rich documents (#2321, #2387).
 *
 * JSON types describe what a trusted producer intends to send; they do not
 * validate a value that crossed the database/API boundary. This parser accepts
 * exactly version 1's finite node vocabulary and rejects the whole document on
 * the first unknown shape. The caller then renders the complete plain-text
 * body. There is deliberately no "best effort" arm: partially interpreting a
 * future node is how meaning (especially warnings and list nesting) gets lost.
 */
export type KbInlineNode =
  | { type: 'text'; text: string }
  | { type: 'strong'; content: KbInlineNode[] }
  | { type: 'emphasis'; content: KbInlineNode[] }
  | { type: 'line_break' }

export type KbDocumentBlock =
  | { type: 'heading'; level: 2 | 3 | 4; content: KbInlineNode[] }
  | { type: 'paragraph'; content: KbInlineNode[] }
  | { type: 'list'; ordered: boolean; items: Array<{ blocks: KbDocumentBlock[] }> }
  | { type: 'table'; header: KbInlineNode[][]; rows: KbInlineNode[][][] }
  | { type: 'callout'; tone: 'note' | 'info' | 'warning'; blocks: KbDocumentBlock[] }
  | { type: 'divider' }
  | { type: 'figure'; figure_id: string; caption: KbInlineNode[] }

export interface KbDocumentV1 {
  version: 1
  blocks: KbDocumentBlock[]
}

interface KbDocumentHeading {
  id: string
  level: 2 | 3 | 4
  text: string
}

const FIGURE_ID = /^[A-Za-z0-9][A-Za-z0-9._:-]{0,63}$/
const MAX_NODES = 10_000
const MAX_DEPTH = 12

type JsonObject = Record<string, unknown>
type Budget = { nodes: number }

function object(value: unknown): JsonObject | null {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
    ? value as JsonObject
    : null
}

function exact(value: JsonObject, keys: string[]): boolean {
  const actual = Object.keys(value).sort()
  return actual.length === keys.length
    && actual.every((key, index) => key === [...keys].sort()[index])
}

function spend(budget: Budget): boolean {
  budget.nodes += 1
  return budget.nodes <= MAX_NODES
}

function inline(value: unknown, budget: Budget, depth: number): KbInlineNode | null {
  const node = object(value)
  if (!node || depth > MAX_DEPTH || !spend(budget) || typeof node.type !== 'string') return null
  if (node.type === 'text') {
    return exact(node, ['type', 'text']) && typeof node.text === 'string'
      ? { type: 'text', text: node.text }
      : null
  }
  if (node.type === 'line_break') {
    return exact(node, ['type']) ? { type: 'line_break' } : null
  }
  if (node.type === 'strong' || node.type === 'emphasis') {
    if (!exact(node, ['type', 'content'])) return null
    const content = inlineList(node.content, budget, depth + 1)
    return content?.length ? { type: node.type, content } : null
  }
  return null
}

function inlineList(value: unknown, budget: Budget, depth: number): KbInlineNode[] | null {
  if (!Array.isArray(value) || value.length === 0) return null
  const result: KbInlineNode[] = []
  for (const valueNode of value) {
    const parsed = inline(valueNode, budget, depth)
    if (!parsed) return null
    result.push(parsed)
  }
  return result
}

function cells(value: unknown, budget: Budget, depth: number): KbInlineNode[][] | null {
  if (!Array.isArray(value) || value.length === 0) return null
  const result: KbInlineNode[][] = []
  for (const cell of value) {
    const parsed = inlineList(cell, budget, depth + 1)
    if (!parsed) return null
    result.push(parsed)
  }
  return result
}

function block(value: unknown, budget: Budget, depth: number): KbDocumentBlock | null {
  const node = object(value)
  if (!node || depth > MAX_DEPTH || !spend(budget) || typeof node.type !== 'string') return null

  if (node.type === 'heading') {
    if (!exact(node, ['type', 'level', 'content']) || ![2, 3, 4].includes(Number(node.level))) return null
    const content = inlineList(node.content, budget, depth + 1)
    return content ? { type: 'heading', level: Number(node.level) as 2 | 3 | 4, content } : null
  }
  if (node.type === 'paragraph') {
    if (!exact(node, ['type', 'content'])) return null
    const content = inlineList(node.content, budget, depth + 1)
    return content ? { type: 'paragraph', content } : null
  }
  if (node.type === 'divider') {
    return exact(node, ['type']) ? { type: 'divider' } : null
  }
  if (node.type === 'figure') {
    if (!exact(node, ['type', 'figure_id', 'caption'])
      || typeof node.figure_id !== 'string' || !FIGURE_ID.test(node.figure_id)) return null
    const caption = inlineList(node.caption, budget, depth + 1)
    return caption ? { type: 'figure', figure_id: node.figure_id, caption } : null
  }
  if (node.type === 'list') {
    if (!exact(node, ['type', 'ordered', 'items'])
      || typeof node.ordered !== 'boolean' || !Array.isArray(node.items) || node.items.length === 0) return null
    const items: Array<{ blocks: KbDocumentBlock[] }> = []
    for (const itemValue of node.items) {
      const item = object(itemValue)
      if (!item || !exact(item, ['blocks'])) return null
      const itemBlocks = blocks(item.blocks, budget, depth + 1)
      if (!itemBlocks) return null
      items.push({ blocks: itemBlocks })
    }
    return { type: 'list', ordered: node.ordered, items }
  }
  if (node.type === 'callout') {
    if (!exact(node, ['type', 'tone', 'blocks'])
      || !['note', 'info', 'warning'].includes(String(node.tone))) return null
    const calloutBlocks = blocks(node.blocks, budget, depth + 1)
    return calloutBlocks
      ? { type: 'callout', tone: node.tone as 'note' | 'info' | 'warning', blocks: calloutBlocks }
      : null
  }
  if (node.type === 'table') {
    if (!exact(node, ['type', 'header', 'rows']) || !Array.isArray(node.rows) || node.rows.length === 0) return null
    const header = cells(node.header, budget, depth + 1)
    if (!header) return null
    const rows: KbInlineNode[][][] = []
    for (const row of node.rows) {
      const parsed = cells(row, budget, depth + 1)
      if (!parsed || parsed.length !== header.length) return null
      rows.push(parsed)
    }
    return { type: 'table', header, rows }
  }
  return null
}

function blocks(value: unknown, budget: Budget, depth: number): KbDocumentBlock[] | null {
  if (!Array.isArray(value) || value.length === 0) return null
  const result: KbDocumentBlock[] = []
  for (const valueBlock of value) {
    const parsed = block(valueBlock, budget, depth)
    if (!parsed) return null
    result.push(parsed)
  }
  return result
}

export function parseKbDocument(value: unknown): KbDocumentV1 | null {
  const document = object(value)
  if (!document || !exact(document, ['version', 'blocks']) || document.version !== 1) return null
  const parsed = blocks(document.blocks, { nodes: 0 }, 0)
  return parsed ? { version: 1, blocks: parsed } : null
}

export function kbInlineText(nodes: KbInlineNode[]): string {
  return nodes.map((node) => node.type === 'text'
    ? node.text
    : node.type === 'line_break'
      ? ' '
      : kbInlineText(node.content)).join('').replace(/\s+/g, ' ').trim()
}

function headingId(text: string, index: number): string {
  const slug = text.toLocaleLowerCase('en')
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
  return `kb-${slug || 'section'}-${index + 1}`
}

/** Top-level headings are the article's table of contents. Nested list/callout
 * headings remain semantic content but do not create a surprising TOC tree. */
export function kbDocumentHeadings(value: unknown): KbDocumentHeading[] {
  const document = parseKbDocument(value)
  if (!document) return []
  return document.blocks.flatMap((item, index) => {
    if (item.type !== 'heading') return []
    const text = kbInlineText(item.content)
    return text ? [{ id: headingId(text, index), level: item.level, text }] : []
  })
}

export function kbDocumentHeadingId(text: string, blockIndex: number): string {
  return headingId(text, blockIndex)
}
